'use client';

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslations } from 'next-intl';
import { IoCalendarOutline, IoChevronDown, IoClose, IoPersonOutline } from 'react-icons/io5';
import type { DateInterval } from '@/types/calendar';
import { formatShortDate } from '@/lib/calendar-utils';
import type { DesignVariant } from './design-variants';
import { FilterCalendar } from './filter-calendar';

// ─── Building labels ────────────────────────────────────────────

export function getBuildingLabels(t: (key: string) => string): Record<string, string> {
  return {
    'daniel-castle': t('buildings.danielCastle'),
    'bethlen-castle': t('buildings.bethlenCastle'),
    'crafts-house': t('buildings.craftsHouse'),
  };
}

export function getSortOptions(t: (key: string) => string) {
  return [
    { value: 'default', label: t('sort.defaultOrder') },
    { value: 'price-asc', label: t('sort.priceLow') },
    { value: 'price-desc', label: t('sort.priceHigh') },
    { value: 'number-asc', label: t('sort.roomNumberAsc') },
    { value: 'number-desc', label: t('sort.roomNumberDesc') },
    { value: 'size-asc', label: t('sort.sizeSmallest') },
    { value: 'size-desc', label: t('sort.sizeLargest') },
    { value: 'capacity-desc', label: t('sort.capacityMost') },
    { value: 'capacity-asc', label: t('sort.capacityLeast') },
  ];
}

// ─── Sidebar Filter Section ────────────────────────────────────

export function SidebarFilterSection({
  label,
  icon,
  children,
}: {
  label: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        onClick={() => setExpanded(e => !e)}
        className="flex items-center justify-between w-full py-3 text-left"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
          {icon}
          {label}
        </span>
        <IoChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-200 ${expanded ? 'max-h-[400px] pb-3' : 'max-h-0'}`}>
        {children}
      </div>
    </div>
  );
}

// ─── Sidebar Filter Option (radio-style) ────────────────────────

export function FilterOption({
  label,
  selected,
  onClick,
  icon,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
  icon?: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-2 py-1.5 rounded-md text-sm transition-colors flex items-center gap-2 ${
        selected
          ? 'bg-[#801515]/10 text-[#801515] font-medium'
          : 'text-gray-600 hover:bg-gray-100'
      }`}
    >
      {icon && <span className="shrink-0 inline-flex">{icon}</span>}
      <span>{label}</span>
    </button>
  );
}

// ─── Filter Dropdown (mobile) ──────────────────────────────────

export function FilterDropdown({
  label,
  icon,
  options,
  selected,
  onSelect,
  variant,
}: {
  label: string;
  icon: React.ReactNode;
  options: { value: string; label: string; icon?: React.ReactNode }[];
  selected: string;
  onSelect: (value: string) => void;
  variant: DesignVariant;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectedLabel = options.find((o) => o.value === selected)?.label || label;
  const hasSelection = selected !== '';

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 border ${
          hasSelection
            ? `${variant.mobileCtaBg} ${variant.mobileCtaText} border-transparent`
            : `${variant.mobileCardBg} ${variant.mobileCardBorder} ${variant.headerText}`
        }`}
      >
        {icon}
        <span className="max-w-[120px] truncate">{hasSelection ? selectedLabel : label}</span>
        {hasSelection ? (
          <IoClose
            className="w-3.5 h-3.5 opacity-70 hover:opacity-100"
            onClick={(e) => { e.stopPropagation(); onSelect(''); setOpen(false); }}
          />
        ) : (
          <IoChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
        )}
      </button>
      {open && (
        <div className={`absolute top-full left-0 mt-1 min-w-[180px] max-h-[280px] overflow-y-auto rounded-lg shadow-xl border z-50 overflow-hidden ${variant.mobileCardBg} ${variant.mobileCardBorder}`}>
          {options.map((option) => (
            <button
              key={option.value}
              onClick={() => { onSelect(option.value); setOpen(false); }}
              className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 transition-colors ${
                selected === option.value
                  ? `${variant.mobileSubtitleColor} font-semibold`
                  : `${variant.headerText} hover:opacity-80`
              }`}
            >
              {option.icon && <span className="shrink-0 inline-flex">{option.icon}</span>}
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Multi-select Filter Dropdown (mobile) ─────────────────────

export function MultiFilterDropdown({
  label,
  icon,
  options,
  selected,
  onToggle,
  onClear,
  variant,
}: {
  label: string;
  icon: React.ReactNode;
  options: { value: string; label: string }[];
  selected: string[];
  onToggle: (value: string) => void;
  onClear: () => void;
  variant: DesignVariant;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const hasSelection = selected.length > 0;
  const buttonLabel = hasSelection
    ? selected.length === 1
      ? options.find((o) => o.value === selected[0])?.label || label
      : `${label} (${selected.length})`
    : label;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 border ${
          hasSelection
            ? `${variant.mobileCtaBg} ${variant.mobileCtaText} border-transparent`
            : `${variant.mobileCardBg} ${variant.mobileCardBorder} ${variant.headerText}`
        }`}
      >
        {icon}
        <span className="max-w-[140px] truncate">{buttonLabel}</span>
        {hasSelection ? (
          <IoClose
            className="w-3.5 h-3.5 opacity-70 hover:opacity-100"
            onClick={(e) => { e.stopPropagation(); onClear(); setOpen(false); }}
          />
        ) : (
          <IoChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
        )}
      </button>
      {open && (
        <div className={`absolute top-full left-0 mt-1 min-w-[200px] max-h-[280px] overflow-y-auto rounded-lg shadow-xl border z-50 overflow-hidden ${variant.mobileCardBg} ${variant.mobileCardBorder}`}>
          {options.map((option) => {
            const isSelected = selected.includes(option.value);
            return (
              <button
                key={option.value}
                onClick={() => onToggle(option.value)}
                className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 transition-colors ${
                  isSelected
                    ? `${variant.mobileSubtitleColor} font-semibold`
                    : `${variant.headerText} hover:opacity-80`
                }`}
              >
                <span className={`w-4 h-4 shrink-0 rounded border flex items-center justify-center ${isSelected ? 'bg-[#801515] border-[#801515]' : 'border-gray-300'}`}>
                  {isSelected && (
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </span>
                <span className="flex-1">{option.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Persons Stepper (desktop sidebar inline) ─────────────────

export function PersonsStepper({
  value,
  onChange,
  max,
  anyLabel,
  unitSingular,
  unitPlural,
  maxLabel,
}: {
  value: number;
  onChange: (n: number) => void;
  max: number;
  anyLabel: string;
  unitSingular: string;
  unitPlural: string;
  maxLabel?: string;
}) {
  const minusDisabled = value <= 0;
  const plusDisabled = value >= max;
  const label = value === 0
    ? anyLabel
    : `${value} ${value === 1 ? unitSingular : unitPlural}`;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2 py-1 px-1">
        <button
          onClick={() => onChange(Math.max(0, value - 1))}
          disabled={minusDisabled}
          aria-label="decrease"
          className="w-8 h-8 rounded-full border border-gray-300 text-gray-600 text-base font-medium disabled:opacity-30 disabled:cursor-not-allowed enabled:hover:border-[#801515] enabled:hover:text-[#801515] transition-colors flex items-center justify-center cursor-pointer"
        >
          −
        </button>
        <span className={`text-sm font-medium ${value > 0 ? 'text-[#801515]' : 'text-gray-600'}`}>
          {label}
        </span>
        <button
          onClick={() => onChange(Math.min(max, value + 1))}
          disabled={plusDisabled}
          aria-label="increase"
          className="w-8 h-8 rounded-full border border-gray-300 text-gray-600 text-base font-medium disabled:opacity-30 disabled:cursor-not-allowed enabled:hover:border-[#801515] enabled:hover:text-[#801515] transition-colors flex items-center justify-center cursor-pointer"
        >
          +
        </button>
      </div>
      {value >= max && maxLabel && (
        <div className="text-[11px] text-gray-400 text-center">{maxLabel}</div>
      )}
    </div>
  );
}

// ─── Mobile Persons Filter (pill button + popover stepper) ─────

export function MobilePersonsFilter({
  value,
  onChange,
  max,
  variant,
  label,
  anyLabel,
  unitSingular,
  unitPlural,
  maxLabel,
}: {
  value: number;
  onChange: (n: number) => void;
  max: number;
  variant: DesignVariant;
  label: string;
  anyLabel: string;
  unitSingular: string;
  unitPlural: string;
  maxLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const hasSelection = value > 0;
  const buttonLabel = hasSelection
    ? `${value} ${value === 1 ? unitSingular : unitPlural}`
    : label;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 border ${
          hasSelection
            ? `${variant.mobileCtaBg} ${variant.mobileCtaText} border-transparent`
            : `${variant.mobileCardBg} ${variant.mobileCardBorder} ${variant.headerText}`
        }`}
      >
        <IoPersonOutline className="w-4 h-4" />
        <span className="max-w-[120px] truncate">{buttonLabel}</span>
        {hasSelection ? (
          <IoClose
            className="w-3.5 h-3.5 opacity-70 hover:opacity-100"
            onClick={(e) => { e.stopPropagation(); onChange(0); setOpen(false); }}
          />
        ) : (
          <IoChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
        )}
      </button>
      {open && (
        <div className={`absolute top-full left-0 mt-1 min-w-[200px] rounded-lg shadow-xl border z-50 p-3 ${variant.mobileCardBg} ${variant.mobileCardBorder}`}>
          <PersonsStepper
            value={value}
            onChange={onChange}
            max={max}
            anyLabel={anyLabel}
            unitSingular={unitSingular}
            unitPlural={unitPlural}
            maxLabel={maxLabel}
          />
        </div>
      )}
    </div>
  );
}

// ─── Mobile Date Filter Button + Calendar dropdown ──────────────

export function MobileDateFilter({
  interval,
  onIntervalChange,
  variant,
  minStayByDate,
}: {
  interval: DateInterval;
  onIntervalChange: (interval: DateInterval) => void;
  variant: DesignVariant;
  /** Global per-date min-stay for the festive auto-select (D30, doc 81 §12.4 D). */
  minStayByDate?: Record<string, number>;
}) {
  const t = useTranslations('rooms');
  const [open, setOpen] = useState(false);
  const hasRange = interval.checkIn && interval.checkOut;

  const displayLabel = hasRange
    ? `${formatShortDate(interval.checkIn!)} \u2013 ${formatShortDate(interval.checkOut!)}`
    : t('dates');

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 border ${
          hasRange
            ? `${variant.mobileCtaBg} ${variant.mobileCtaText} border-transparent`
            : `${variant.mobileCardBg} ${variant.mobileCardBorder} ${variant.headerText}`
        }`}
      >
        <IoCalendarOutline className="w-4 h-4" />
        <span className="max-w-[160px] truncate">{displayLabel}</span>
        {hasRange ? (
          <IoClose
            className="w-3.5 h-3.5 opacity-70 hover:opacity-100"
            onClick={(e) => { e.stopPropagation(); onIntervalChange({ checkIn: null, checkOut: null }); setOpen(false); }}
          />
        ) : (
          <IoChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
        )}
      </button>

      {/* Modal backdrop + calendar panel — portalled to body to escape stacking context */}
      {open && createPortal(
        <>
          <div
            className="fixed inset-0 z-[48] bg-black/25 backdrop-blur-[2px]"
            onClick={() => setOpen(false)}
          />
          <div className="fixed top-[84px] left-3 z-[49] w-[300px] rounded-xl shadow-2xl border border-gray-200 p-4 bg-white">
            <FilterCalendar interval={interval} onIntervalChange={onIntervalChange} minStayByDate={minStayByDate} />
            <div className="flex items-center justify-between mt-3">
              {hasRange ? (
                <button
                  onClick={() => onIntervalChange({ checkIn: null, checkOut: null })}
                  className="text-xs underline text-gray-400"
                >
                  {t('clearDates')}
                </button>
              ) : <span />}
              <button
                onClick={() => setOpen(false)}
                className="px-4 py-1.5 rounded-md text-xs font-semibold bg-[#801515] text-white hover:bg-[#601010] transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}

// ─── Helper: extract prices from bulk calendar data ─────────────

/**
 * Pull each room's display price out of bulk calendar data. Prefers the
 * server-emitted `minPrice` (which already includes the next-month
 * fallback when the displayed month has no usable price); falls back to
 * a non-past day price across the visible month for older payloads.
 */
export function extractPricesFromBulk(
  rooms: Record<string, { days: { available: boolean; isPast: boolean; price: number | null }[]; currency: string; minPrice?: number | null }>
): Record<string, { price: number | null; currency: string }> {
  const result: Record<string, { price: number | null; currency: string }> = {};
  for (const [slug, data] of Object.entries(rooms)) {
    let price: number | null = data.minPrice ?? null;
    if (price === null) {
      const fallback = data.days
        .filter(d => !d.isPast && d.price !== null)
        .map(d => d.price as number);
      if (fallback.length > 0) price = Math.min(...fallback);
    }
    result[slug] = { price, currency: data.currency };
  }
  return result;
}
